import React, { useRef, useState, useEffect } from 'react';
import { Label } from 'app/components/ui/label';
import { Button } from 'app/components/ui/button';
import { useFormContext } from 'react-hook-form';
import { useMediaSlice } from 'app/pages/Media/slice';
import { useToast } from 'app/components/ui/use-toast';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Image,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Type,
  Code,
  Heading1,
  Heading2,
  Heading3,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';

export default function ProductDescription() {
  const form = useFormContext();
  const { useUploadMediaMutation } = useMediaSlice();
  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();
  const { toast } = useToast();

  // Image upload handler
  const handleImageUpload = async (file: File) => {
    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Image size must be less than 5MB',
          variant: 'destructive',
        });
        return null;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please select a valid image file',
          variant: 'destructive',
        });
        return null;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'product-descriptions');

      const response = await uploadMedia(formData).unwrap();

      if (response?.data?.url) {
        toast({
          title: 'Success',
          description: 'Image uploaded successfully',
          variant: 'default',
        });
        return response.data.url;
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    }
    return null;
  };

  // Short Description Editor
  const shortDescEditor = useEditor({
    extensions: [
      StarterKit,
      ImageExtension.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
    ],
    content: form.watch('short_description') || '',
    onUpdate: ({ editor }) => {
      form.setValue('short_description', editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-base max-w-none mx-auto focus:outline-none min-h-[120px] p-3 text-sm',
        style: 'font-size: 14px;',
      },
    },
  });

  // Full Description Editor
  const descEditor = useEditor({
    extensions: [
      StarterKit,
      ImageExtension.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
    ],
    content: form.watch('description') || '',
    onUpdate: ({ editor }) => {
      form.setValue('description', editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-base max-w-none mx-auto focus:outline-none min-h-[180px] p-3 text-sm',
        style: 'font-size: 14px;',
      },
    },
  });

  // Update editor content when form values change
  useEffect(() => {
    if (
      shortDescEditor &&
      form.watch('short_description') !== shortDescEditor.getHTML()
    ) {
      shortDescEditor.commands.setContent(
        form.watch('short_description') || '',
      );
    }
  }, [form.watch('short_description'), shortDescEditor]);

  useEffect(() => {
    if (descEditor && form.watch('description') !== descEditor.getHTML()) {
      descEditor.commands.setContent(form.watch('description') || '');
    }
  }, [form.watch('description'), descEditor]);

  // Image upload function
  const addImage = async (editor: any) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const imageUrl = await handleImageUpload(file);
      if (imageUrl) {
        editor.chain().focus().setImage({ src: imageUrl }).run();
      }
    };
  };

  // Link function
  const addLink = (editor: any) => {
    const url = prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  // Toolbar component
  const Toolbar = ({ editor }: { editor: any }) => {
    if (!editor) return null;

    return (
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-600' : ''
            }`}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-600' : ''
            }`}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('underline') ? 'bg-gray-200 dark:bg-gray-600' : ''
            }`}
          title="Underline"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 1 })
              ? 'bg-gray-200 dark:bg-gray-600'
              : ''
            }`}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 2 })
              ? 'bg-gray-200 dark:bg-gray-600'
              : ''
            }`}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 3 })
              ? 'bg-gray-200 dark:bg-gray-600'
              : ''
            }`}
          title="Heading 3"
        >
          <Heading3 className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-600' : ''
            }`}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-600' : ''
            }`}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => addLink(editor)}
          className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-gray-200 dark:bg-gray-600' : ''
            }`}
          title="Insert Link"
        >
          <Link className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => addImage(editor)}
          className="h-8 w-8 p-0"
          title="Insert Image"
          disabled={isUploading}
        >
          <Image className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h7"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Product Description
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Provide detailed product information
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="space-y-3">
          <Label
            htmlFor="short-description"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mr-2"></span>
            Short Description
          </Label>
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-700">
            <Toolbar editor={shortDescEditor} />
            <EditorContent editor={shortDescEditor} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Brief summary displayed in product listings
          </p>
        </div>

        <div className="space-y-3">
          <Label
            htmlFor="description"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mr-2"></span>
            Full Description
          </Label>
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-700">
            <Toolbar editor={descEditor} />
            <EditorContent editor={descEditor} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Detailed product information and features. You can upload images
            directly in the editor.
          </p>
        </div>
      </div>
    </div>
  );
}
